import { Link, NavLink } from "react-router-dom";
import { useLayoutEffect } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./providers/AuthProvider";
import { truncate, PROGRAMS, SOLSCAN_TX } from "./Primitives";
import { WordmarkAltheia } from "./Logo";
import { useTheme } from "@/hooks/useTheme";

const GITHUB_URL = "https://github.com/altheia-xyz";
const X_URL = "https://x.com/altheiaxyz";
const DOCS_URL = "https://docs.altheia.xyz";

function ThemeToggle() {
  const [theme, , toggle] = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      className="chrome__theme"
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      title={isDark ? "Light theme" : "Dark theme"}
    >
      {isDark ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

function XIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/**
 * Single source of truth for top nav + footer across the whole app.
 *   mode="app"    — product chrome: AGENTS / POLICIES / CHRONICLE / ORG nav,
 *                   devnet pill, wallet truncated, sign-out
 *   mode="public" — marketing chrome: PROBLEM / HOW / DEMO / DOCS nav,
 *                   GitHub external + "View live demo" CTA
 *
 * Logo always routes to "/" — public landing — regardless of mode.
 */
export function AppChrome({
  children,
  mode = "app",
}: {
  children: ReactNode;
  mode?: "app" | "public";
}) {
  const { operator, signOut } = useAuth();

  // Marketing pages stay light regardless of the operator's app-side preference.
  useLayoutEffect(() => {
    if (mode !== "public") return;
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.removeAttribute("data-theme");
    return () => {
      if (prev) document.documentElement.setAttribute("data-theme", prev);
    };
  }, [mode]);

  return (
    <div className="app">
      <header className="app__chrome">
        <div className="chrome__left">
          <Link to="/" className="wordmark" aria-label="Altheia home">
            <WordmarkAltheia size={22} />
          </Link>
          {mode === "app" && (
            <nav className="chrome__nav">
              <NavLink to="/dashboard" className="chrome__nav-link">AGENTS</NavLink>
              <NavLink to="/audit" className="chrome__nav-link">CHRONICLE</NavLink>
              <NavLink to="/settings" className="chrome__nav-link">ORG</NavLink>
            </nav>
          )}
        </div>
        <div className="chrome__right">
          {mode === "app" ? (
            <>
              <span className="chrome__cluster">
                <span className="chrome__cluster-dot" /> devnet
              </span>
              {operator && (
                <span className="chrome__operator">
                  <span className="num">{truncate(operator.wallet_pubkey)}</span>
                  <button className="chrome__signout" onClick={signOut}>sign out</button>
                </span>
              )}
              <ThemeToggle />
            </>
          ) : (
            <>
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="chrome__nav-link"
              >
                DOCS
              </a>
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="chrome__nav-link chrome__nav-link--icon"
                aria-label="Altheia on X"
              >
                <XIcon />
              </a>
            </>
          )}
        </div>
      </header>
      <main className={`app__main ${mode === "public" ? "app__main--public" : ""}`}>{children}</main>
      <footer className="app__footer">
        <div className="footer__left">
          <span className="footer__brand">
            <WordmarkAltheia size={16} />
          </span>
          <span className="footer__meta"> · v0.1</span>
        </div>
        <div className="footer__center">
          <span className="footer__meta">non-custodial · on-chain enforced · instantly revocable</span>
        </div>
        <div className="footer__right">
          <a href={DOCS_URL} className="footer__link" target="_blank" rel="noopener noreferrer">DOCS</a>
          <a href={GITHUB_URL} className="footer__link" target="_blank" rel="noopener noreferrer">GITHUB</a>
          <a href={X_URL} className="footer__link footer__link--icon" target="_blank" rel="noopener noreferrer" aria-label="Altheia on X">
            <XIcon />
          </a>
          <a href={SOLSCAN_TX(PROGRAMS.genesisDeployTx)} className="footer__link" target="_blank" rel="noopener noreferrer">STATUS</a>
          <a href="#" className="footer__link">PRIVACY</a>
        </div>
      </footer>
      <style>{`
        .app { min-height: 100vh; display: grid; grid-template-rows: auto 1fr auto; }
        .app__chrome {
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          gap: var(--s-5);
          padding: var(--s-4) var(--s-6);
          border-bottom: 1px solid var(--line-soft);
          background: var(--bg);
        }
        .chrome__left { display: flex; align-items: center; gap: var(--s-7); min-width: 0; }
        .wordmark { font: var(--type-display-section); font-style: italic; }
        .wordmark__brand { color: var(--bone); }
        .chrome__nav { display: flex; gap: var(--s-5); }
        .chrome__nav-link {
          font: var(--type-label);
          letter-spacing: var(--tracking-label);
          text-transform: uppercase;
          color: var(--slate);
          padding: var(--s-1) 0;
          border-bottom: 1px solid transparent;
          font-size: 11px;
        }
        .chrome__nav-link:hover { color: var(--bone); }
        .chrome__nav-link.active { color: var(--bone); border-bottom-color: var(--bronze); }
        .chrome__nav-link--icon { display: inline-flex; align-items: center; }
        .chrome__right { display: flex; align-items: center; gap: var(--s-5); }
        .chrome__cluster {
          font: var(--type-mono-data);
          color: var(--slate-2);
          display: inline-flex;
          align-items: center;
          gap: var(--s-2);
        }
        .chrome__cluster-dot {
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: var(--bronze);
          animation: ds-pulse var(--dur-pulse) ease-in-out infinite;
        }
        .chrome__operator { display: inline-flex; align-items: center; gap: var(--s-3); font-size: 12px; color: var(--slate); }
        .chrome__signout { color: var(--slate); border-bottom: 1px solid var(--line-soft); padding-bottom: 1px; font-size: 11px; }
        .chrome__signout:hover { color: var(--bone); }
        .chrome__cta {
          background: var(--bone);
          color: var(--bg);
          padding: 8px 14px;
          font: var(--type-label);
          letter-spacing: var(--tracking-label);
          text-transform: uppercase;
          font-size: 11px;
          transition: background 160ms;
        }
        .chrome__cta:hover { background: var(--bronze-deep); }
        .app__main { padding: var(--s-6); max-width: var(--page-max); margin: 0 auto; width: 100%; }
        .app__main--public { padding: 0; max-width: none; }
        .app__footer {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          gap: var(--s-5);
          padding: var(--s-5) var(--s-6);
          border-top: 1px solid var(--line-soft);
          font: var(--type-mono-address);
          color: var(--slate-2);
        }
        .footer__center { text-align: center; }
        .footer__greek { font: var(--type-display-section); font-style: italic; color: var(--bone); font-size: 18px; }
        .footer__right { display: flex; gap: var(--s-4); justify-content: flex-end; }
        .footer__link { color: var(--slate); border-bottom: 1px solid transparent; padding-bottom: 1px; }
        .footer__link:hover { color: var(--bone); border-bottom-color: var(--line); }
        .footer__link--icon { display: inline-flex; align-items: center; padding-bottom: 2px; }
        .footer__brand { display: inline-flex; align-items: center; }
        .chrome__theme {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          color: var(--slate);
          border: 1px solid var(--line-soft);
          background: transparent;
          transition: color 160ms, border-color 160ms;
        }
        .chrome__theme:hover { color: var(--bone); border-color: var(--line); }
        @media (max-width: 768px) {
          .app__chrome { grid-template-columns: 1fr; }
          .chrome__left { gap: var(--s-4); flex-wrap: wrap; }
          .chrome__nav { gap: var(--s-3); }
          .app__footer { grid-template-columns: 1fr; text-align: center; }
          .footer__right { justify-content: center; }
        }
      `}</style>
    </div>
  );
}
